import { z } from "zod";

/**
 * Business email blocklist — personal email providers rejected on both
 * frontend (this file) and BFF (validators/application.ts).
 */
const PERSONAL_EMAIL_DOMAINS = [
	"gmail.com",
	"googlemail.com",
	"yahoo.com",
	"yahoo.co.uk",
	"hotmail.com",
	"hotmail.co.uk",
	"outlook.com",
	"live.com",
	"msn.com",
	"aol.com",
	"icloud.com",
	"me.com",
	"mac.com",
	"protonmail.com",
	"proton.me",
	"mail.com",
	"zoho.com",
	"yandex.com",
	"gmx.com",
	"gmx.net",
	"fastmail.com",
	"tutanota.com",
	"qq.com",
	"163.com",
	"126.com",
	"naver.com",
	"daum.net",
];

export const businessEmailSchema = z
	.string()
	.email("Invalid email address")
	.refine(
		(email) => {
			const domain = email.split("@")[1]?.toLowerCase();
			return domain ? !PERSONAL_EMAIL_DOMAINS.includes(domain) : false;
		},
		{
			message:
				"Please use your company email address. Personal email providers are not accepted.",
		},
	);

const contactSchema = z.object({
	name: z.string().min(1, "Contact name is required"),
	email: businessEmailSchema,
	phone: z.string().min(1, "Phone number is required"),
	role: z.string().min(1, "Role is required"),
});

// ── Step 1: Company & Wallet Info ──────────────────────────────
export const companyWalletSchema = z.object({
	company_name: z.string().min(1, "Company name is required"),
	wallet_name: z.string().min(1, "Wallet name is required"),
	custodial_type: z.string().min(1, "Custodial type is required"),
	chain: z.string().min(1, "Chain is required"),
	asset: z.string().min(1, "Asset is required"),
	products: z.array(z.string()).min(1, "At least one product is required"),
	country_of_incorporation: z.string().min(1, "Country is required"),
	website: z.string().url("Invalid URL").optional().or(z.literal("")),
	business_description: z
		.string()
		.min(10, "Please provide a business description (min 10 characters)"),
});

// ── Step 2: Contact Details ────────────────────────────────────
export const contactDetailsSchema = z.object({
	primary_contact: contactSchema,
	secondary_contact: contactSchema.optional(),
});

// ── Step 3: Review & Submit ────────────────────────────────────
export const reviewSchema = z.object({
	terms_accepted: z.boolean().refine((val) => val === true, {
		message: "You must accept the terms and conditions",
	}),
});

/** Per-step validation schemas, indexed by step number (1-based). */
export const stepSchemas = {
	1: companyWalletSchema,
	2: contactDetailsSchema,
	3: reviewSchema,
} as const;

/** Full application validation for final submit. */
export const fullApplicationSchema = companyWalletSchema
	.merge(contactDetailsSchema)
	.merge(reviewSchema);

export type CompanyWalletFormData = z.infer<typeof companyWalletSchema>;
export type ContactDetailsFormData = z.infer<typeof contactDetailsSchema>;
export type ReviewFormData = z.infer<typeof reviewSchema>;
export type FullApplicationFormData = z.infer<typeof fullApplicationSchema>;

export { PERSONAL_EMAIL_DOMAINS };
